import {Controller, useForm} from 'react-hook-form';
import {CompanyRequest, Company} from '../../../models/company';
import {
  Checkbox,
} from 'components/form-items';
import {useMutation, useQueryClient} from 'react-query';
import {api} from '../../../utils/api';
import {
  FormItem,
  Input,
  Radiobox,
  Modal,
  Button,
  Tooltip,
  toast,
} from '@mayo/mayo-ui-gakki';
import { validTaxIDNumber } from '@mayo/utils';
import {ReactComponent as InfoIcon} from '@common/assets/icon_info-circle.svg';

type CreateFormProps = {
  isOpen?: boolean;
  onClose?: () => void;
  type?: 'create' | 'edit' | 'active' | 'hidden';
  defaultValue?: Company;
};

function CompanyForm({
  isOpen,
  onClose,
  type = 'create',
  defaultValue = {
    feature: {
      enableStayFun: false,
      enableFacePass: false,
      enableClockIn: false,
    },
  } as Company,
}: CreateFormProps) {
  const client = useQueryClient();
  const {mutateAsync: onSubmit} = useMutation(
    (data: CompanyRequest): Promise<void> => {
      if (type === 'create') {
        data.progressState = 0
        return api.post('backoffice/company', {data: {...data, enablePY: String(data.enablePY) === 'true' ? true : false }});
      } else if (type === 'edit') {
        data.progressState = Number(data.progressState || 0)
        return api.put(`backoffice/company/${data.id}`, {data: {...data, enablePY: String(data.enablePY) === 'true' ? true : false }});
      }
      return Promise.resolve();
    },
    {
      onSuccess: () => {
        onClose && onClose();
        client.invalidateQueries('companies');
      },
    },
  );

  const {
    register,
    handleSubmit,
    control,
    formState: {errors, isSubmitting, isSubmitted},
  } = useForm<CompanyRequest>();

  return (
    <form
      className={'company-form'}
    >
      <Modal
        modalLabel="company-dialog"
        modalContentLabel="form-content"
        isOpen={isOpen}
        size="md"
        modalTitle={type === 'create' ? '新增專區' : '編輯專區'}
        onTitleClose={onClose}
        onConfirm={!defaultValue?.isFinished ? handleSubmit((data: CompanyRequest) => {
          return toast.promise(() => onSubmit(data), {
            pending: 'loading',
            success: type === 'create' ? '專區建立成功' : '專區更新成功',
          })
        }) : undefined}
        onCancel={onClose}
        disabled={isSubmitting}
      >
        <input
          type={'hidden'}
          ref={register}
          name={'id'}
          defaultValue={defaultValue.id}
        />
        <input
          type={'hidden'}
          ref={register}
          name={'progressState'}
          defaultValue={defaultValue.progressState}
        />
        <div className="grid grid-cols-2 gap-[20px]">
          <FormItem
            required
            label="公司代碼"
            htmlFor="code"
            errorMessage={isSubmitted && errors?.code?.message}
          >
            <Input
              name={'code'}
              id={'code'}
              placeholder={'請輸入公司代碼'}
              ref={register({
                required: '公司代碼為必填',
                pattern: {
                  value: /^[A-Za-z0-9]{3,10}$/,
                  message: '公司代碼限定 3 ~ 10 碼英文或數字',
                },
              })}
              maxLength={10}
              isError={Boolean(isSubmitted && errors?.code?.message)}
              defaultValue={defaultValue?.code}
              disabled={isSubmitting || defaultValue?.isFinished}
              autoFocus
            />
          </FormItem>
          <FormItem
            required
            label="公司名稱"
            htmlFor="name"
            errorMessage={isSubmitted && errors?.name?.message}
          >
            <Input
              name={'name'}
              id={'name'}
              placeholder={'請輸入公司名稱'}
              ref={register({required: '公司名稱為必填'})}
              isError={Boolean(isSubmitted && errors?.name?.message)}
              defaultValue={defaultValue?.name}
              disabled={isSubmitting || defaultValue?.isFinished}
            />
          </FormItem>
          <FormItem
            required
            label="統一編號"
            htmlFor="vat"
            errorMessage={isSubmitted && errors?.vat?.message}
          >
            <Input
              placeholder={'請輸入統一編號'}
              name={'vat'}
              id={'vat'}
              ref={register({
                required: '統一編號為必填',
                pattern: {
                  value: /^\d{8}$/,
                  message: '統一編號長度限制為 8 碼數字',
                },
                validate: {
                  isValid: v => validTaxIDNumber(v) || '請輸入正確統一編號',
                }
              })}
              isError={Boolean(isSubmitted && errors?.vat?.message)}
              defaultValue={defaultValue?.vat}
              disabled={isSubmitting || defaultValue?.isFinished}
              maxLength={8}
            />
          </FormItem>
          <FormItem
            required
            label="購買授權人數"
            htmlFor="amountOfLicense"
            errorMessage={isSubmitted && errors?.amountOfLicense?.message}
          >
            <Input
              placeholder={'請輸入授權人數'}
              type={'number'}
              name={'amountOfLicense'}
              id={'amountOfLicense'}
              ref={register({
                required: '授權人數為必填',
                minLength: {
                  message: '授權人數不可小於 1',
                  value: 1,
                },
              })}
              isError={Boolean(isSubmitted && errors?.amountOfLicense?.message)}
              defaultValue={defaultValue?.amountOfLicense}
              disabled={isSubmitting || defaultValue?.isFinished}
              min={1}
            />
          </FormItem>
          <FormItem
            required
            label="導入精靈登入帳號"
            htmlFor="account"
            errorMessage={isSubmitted && errors?.account?.message}
          >
            <Input
              name={'account'}
              id={'account'}
              placeholder={'請輸入登入帳號'}
              ref={register({required: '登入帳號為必填'})}
              isError={Boolean(isSubmitted && errors?.account?.message)}
              defaultValue={defaultValue?.account}
              disabled={isSubmitting || defaultValue?.isFinished}
            />
          </FormItem>
          <FormItem
            label={
              <div className="flex items-center gap-[5px]">
                智慧結薪模組
                <Tooltip content="關閉後啟用專區，XE的智慧結薪模組仍可操作。" isIcon>
                  <Button
                    variant='simple'
                    colorSchema='primary'
                    icon={<InfoIcon/>}
                  />
                </Tooltip>
              </div>
            }
            htmlFor="enablePY"
          >
            <Controller
              name={'enablePY'}
              control={control}
              defaultValue={typeof defaultValue?.feature.enablePY === 'boolean' ? defaultValue?.feature.enablePY : true}
              render={props => (
                <Radiobox.Group
                  onChange={(e: any) => {
                    props.onChange(e.target.value)
                  }}
                  value={String(props.value)}
                  name={props.name}
                  disabled={isSubmitting || defaultValue?.isFinished}
                  radioboxs={[
                    {
                      name: "enablePY-yes",
                      value: "true",
                      children: "開啟",
                    },
                    {
                      name: "enablePY-no",
                      value: "false",
                      children: "關閉",
                    },
                  ]}
                />
              )}
            />
          </FormItem>
        </div>
        <div className={'hidden'}>
          <label>服務啟用</label>
          <div
            className={'flex items-center gap-4'}
          >
            <Controller
              name={'enableFacePass'}
              control={control}
              defaultValue={defaultValue?.feature.enableFacePass || false}
              render={props => (
                <Checkbox
                  labelName={'臉部辨識打卡'}
                  checked={props.value}
                  onChange={e => props.onChange(e.target.checked)}
                  disabled={isSubmitting || defaultValue?.isFinished}
                />
              )}
            />
            <Controller
              name={'enableClockIn'}
              control={control}
              defaultValue={defaultValue?.feature.enableClockIn || true}
              render={props => (
                <Checkbox
                  labelName={'卡鐘資料匯入'}
                  checked={props.value}
                  onChange={e => props.onChange(e.target.checked)}
                  disabled={isSubmitting || defaultValue?.isFinished}
                />
              )}
            />
            <Controller
              name={'enableStayFun'}
              control={control}
              defaultValue={defaultValue?.feature.enableStayFun || true}
              render={props => (
                <Checkbox
                  labelName={'STAYFUN'}
                  checked={props.value}
                  onChange={e => props.onChange(e.target.checked)}
                  disabled={isSubmitting || defaultValue?.isFinished}
                />
              )}
            />
          </div>
        </div>
      </Modal>
    </form>
  );
}

export default CompanyForm;
